// Tekst van de meldingen die de nachtelijke controles op deelnemers maken:
// een verlopend identiteitsbewijs, een verjaardag, en een meetmoment dat bijna
// zover of te laat is. De naam staat apart, zodat de lijst hem vet kan zetten.
//
// Spiegel van alertSentence in functions/personas/dateAlertText.js en van
// momentAlertSegments in functions/personas/momentAlertText.js — dezelfde
// zinnen, in de mail en achter het belicoon. De functions-kant is de BRON
// (dát wordt verstuurd); functions/personas/momentAlertTextParity.test.js
// houdt deze spiegel eraan vast.
// AI-CONTEXT: See docs/patterns.md#data-mirrored-constants
// AI-CONTEXT: See docs/features/persona-datum-meldingen.md
// AI-CONTEXT: See docs/features/meetmoment-melding.md

import { formatDueCountdown } from '../measurementSignals/describeSignal'

/** De Type-waarden op Notifications, zoals de Cloud Functions ze schrijven. */
const EXPIRY_TYPE = 'persona-document-expiry'
const BIRTHDAY_TYPE = 'persona-birthday'
const MOMENT_DUE_TYPE = 'persona-moment-due'
const MOMENT_OVERDUE_TYPE = 'persona-moment-overdue'

/**
 * Het veld op CompagnyMeta waarmee een organisatie de meetmomentregels in de
 * dagmail aanzet. Spiegel van MOMENT_MAIL_FIELD in
 * functions/personas/sendDailyPersonaMail.js.
 */
const MOMENT_MAIL_FIELD = 'MeasurementMomentMail'

/**
 * @param {string} [iso] - `YYYY-MM-DD`
 * @returns {string} `DD-MM-YYYY`, of '' als het geen datum is
 */
const formatIsoDateNl = (iso) => {
    const m = typeof iso === 'string' ? iso.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null
    if (!m) return ''
    return `${m[3]}-${m[2]}-${m[1]}`
}

/**
 * @typedef {object} PersonaAlertMelding
 * @property {string} Type - EXPIRY_TYPE of BIRTHDAY_TYPE
 * @property {string} [PersonaName] - naam van de deelnemer; leeg als die niet bekend was
 * @property {string} [Date] - de verloop- of geboortedatum als `YYYY-MM-DD`
 * @property {number} [DaysLeft] - alleen bij verlopen: 30 of 0
 * @property {number} [Age] - alleen bij een verjaardag
 */

/**
 * @typedef {object} MomentAlertMelding
 * @property {string} Type - MOMENT_DUE_TYPE of MOMENT_OVERDUE_TYPE
 * @property {string} [PersonaName] - naam van de deelnemer; leeg als die niet bekend was
 * @property {string} [MomentTitle] - titel van het meetmoment dat openstaat
 * @property {string} [ResearchTitle] - titel van het onderzoek
 * @property {number|null} [DaysUntilDue] - negatief = te laat
 */

/**
 * @typedef {object} PersonaAlertSegments
 * @property {string} heading - de kop van de melding
 * @property {string} before - tekst vóór de naam ('' bij een verjaardag)
 * @property {string} name - de naam van de deelnemer, of een terugval
 * @property {string} after - tekst na de naam
 */

/**
 * @param {string} [type]
 * @returns {boolean} of dit een datummelding over een deelnemer is
 */
const isPersonaAlertType = (type) => type === EXPIRY_TYPE || type === BIRTHDAY_TYPE

/**
 * @param {string} [type]
 * @returns {boolean} of dit een melding over een meetmoment is
 */
const isMomentAlertType = (type) => type === MOMENT_DUE_TYPE || type === MOMENT_OVERDUE_TYPE

/**
 * Splitst de datummelding in kop en zin, met de naam los.
 *
 * @param {PersonaAlertMelding} melding
 * @returns {PersonaAlertSegments}
 */
const personaAlertSegments = (melding) => {
    const name = (melding?.PersonaName || '').trim() || 'Een deelnemer'
    const datum = formatIsoDateNl(melding?.Date)

    if (melding?.Type === BIRTHDAY_TYPE) {
        const leeftijd = Number.isFinite(melding.Age) ? ` en wordt ${melding.Age}` : ''
        return { heading: 'Verjaardag', before: '', name, after: ` is vandaag jarig${leeftijd}.` }
    }

    const wanneer = melding?.DaysLeft === 0
        ? 'verloopt vandaag'
        : `verloopt over ${melding?.DaysLeft ?? '?'} dagen`
    const metDatum = datum ? ` (${datum})` : ''
    return {
        heading: 'Identiteitsbewijs verloopt',
        before: 'Het identiteitsbewijs van ',
        name,
        after: ` ${wanneer}${metDatum}.`,
    }
}

/**
 * Splitst de meetmomentmelding in kop en zin, met de naam los. Dezelfde
 * aftelling als de dashboardkaart, dus nooit een minteken.
 *
 * @param {MomentAlertMelding} melding
 * @returns {PersonaAlertSegments}
 */
const momentAlertSegments = (melding) => {
    const name = (melding?.PersonaName || '').trim() || 'een deelnemer'
    const moment = (melding?.MomentTitle || '').trim() || 'het volgende meetmoment'
    const onderzoek = (melding?.ResearchTitle || '').trim()
    const van = onderzoek ? ` van '${onderzoek}'` : ''
    const aftelling = formatDueCountdown(melding?.DaysUntilDue)
    const bekend = aftelling !== 'onbekend'

    if (melding?.Type === MOMENT_OVERDUE_TYPE) {
        return {
            heading: 'Meetmoment te laat',
            before: `Het meetmoment '${moment}'${van} voor `,
            name,
            after: bekend ? ` is ${aftelling}.` : ' is te laat.',
        }
    }
    // 'Bijna zover' loopt tot 1,25x de tussentijd, de aftelling maar tot
    // 1,0x. Daartussen is de dag al gepasseerd terwijl de rij nog niet 'te
    // laat' heet; dan geen 'bijna … (10 dagen te laat)' maar 'aan de beurt
    // (sinds 10 dagen)'. Gemeten 2026-10-01: 4 van de 10 due-meldingen.
    const dagen = melding?.DaysUntilDue
    if (typeof dagen === 'number' && Number.isFinite(dagen) && dagen < 0) {
        const sinds = Math.abs(dagen)
        return {
            heading: 'Meetmoment bijna zover',
            before: 'Voor ',
            name,
            after: ` is het meetmoment '${moment}'${van} aan de beurt (sinds ${sinds} ${sinds === 1 ? 'dag' : 'dagen'}).`,
        }
    }
    return {
        heading: 'Meetmoment bijna zover',
        before: 'Voor ',
        name,
        after: ` is het meetmoment '${moment}'${van} bijna aan de beurt${bekend ? ` (${aftelling})` : ''}.`,
    }
}

export {
    EXPIRY_TYPE,
    BIRTHDAY_TYPE,
    MOMENT_DUE_TYPE,
    MOMENT_OVERDUE_TYPE,
    MOMENT_MAIL_FIELD,
    formatIsoDateNl,
    isPersonaAlertType,
    isMomentAlertType,
    personaAlertSegments,
    momentAlertSegments,
}
export default personaAlertSegments
